import { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const JobDetailsSection = ({ formData, onChange, errors }) => {
  const [showSalaryTips, setShowSalaryTips] = useState(false);

  const employmentTypes = [
    { value: 'full-time', label: 'Full Time' },
    { value: 'part-time', label: 'Part Time' },
    { value: 'contract', label: 'Contract' },
    { value: 'internship', label: 'Internship' },
    { value: 'freelance', label: 'Freelance' }
  ];

  const experienceLevels = [
    { value: 'entry', label: 'Entry Level (0-2 years)' },
    { value: 'mid', label: 'Mid Level (2-5 years)' },
    { value: 'senior', label: 'Senior Level (5-10 years)' },
    { value: 'lead', label: 'Lead/Principal (10+ years)' },
    { value: 'executive', label: 'Executive/C-Level' }
  ];

  const departments = [
    { value: 'engineering', label: 'Engineering & Technology' },
    { value: 'sales', label: 'Sales & Business Development' },
    { value: 'marketing', label: 'Marketing & Communications' },
    { value: 'hr', label: 'Human Resources' },
    { value: 'finance', label: 'Finance & Accounting' },
    { value: 'operations', label: 'Operations & Supply Chain' },
    { value: 'design', label: 'Design & Creative' },
    { value: 'customer-service', label: 'Customer Service & Support' },
    { value: 'legal', label: 'Legal & Compliance' },
    { value: 'other', label: 'Other' }
  ];

  const salaryRanges = [
    { value: '0-300000', label: '₹0 - ₹3,00,000' },
    { value: '300000-600000', label: '₹3,00,000 - ₹6,00,000' },
    { value: '600000-1000000', label: '₹6,00,000 - ₹10,00,000' },
    { value: '1000000-1500000', label: '₹10,00,000 - ₹15,00,000' },
    { value: '1500000-2500000', label: '₹15,00,000 - ₹25,00,000' },
    { value: '2500000+', label: '₹25,00,000+' }
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="Briefcase" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground">Job Details</h2>
          <p className="text-sm text-muted-foreground">Essential information about the position</p>
        </div>
      </div>
      <div className="space-y-6">
        <Input
          label="Job Title"
          type="text"
          placeholder="e.g., Senior React Developer"
          value={formData?.jobTitle}
          onChange={(e) => onChange('jobTitle', e?.target?.value)}
          error={errors?.jobTitle}
          required
          description="Be specific and clear about the role"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Department"
            options={departments}
            value={formData?.department}
            onChange={(value) => onChange('department', value)}
            error={errors?.department}
            required
            placeholder="Select department"
          />

          <Select
            label="Employment Type"
            options={employmentTypes}
            value={formData?.employmentType}
            onChange={(value) => onChange('employmentType', value)}
            error={errors?.employmentType}
            required
            placeholder="Select employment type"
          />
        </div>

        <Select
          label="Experience Level"
          options={experienceLevels}
          value={formData?.experienceLevel}
          onChange={(value) => onChange('experienceLevel', value)}
          error={errors?.experienceLevel}
          required
          placeholder="Select required experience level"
          description="Choose the minimum experience required for this role"
        />

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-foreground">
              Salary Range (Annual) <span className="text-error">*</span>
            </label>
            <button
              type="button"
              onClick={() => setShowSalaryTips(!showSalaryTips)}
              className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-smooth"
            >
              <Icon name="Info" size={16} />
              <span>Salary Tips</span>
            </button>
          </div>

          {showSalaryTips && (
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
              <div className="flex gap-3">
                <Icon name="Lightbulb" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                <div className="space-y-2 text-sm text-foreground">
                  <p className="font-medium">Competitive Salary Insights:</p>
                  <ul className="space-y-1 list-disc list-inside text-muted-foreground">
                    <li>Transparent salary ranges attract 30% more qualified candidates</li>
                    <li>Consider market rates for similar roles in your location</li>
                    <li>Include benefits value when determining total compensation</li>
                    <li>Be prepared to negotiate within your stated range</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          <Select
            options={salaryRanges}
            value={formData?.salaryRange}
            onChange={(value) => onChange('salaryRange', value)}
            error={errors?.salaryRange}
            required
            placeholder="Select salary range"
            description="Showing competitive salary ranges helps attract qualified candidates"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Number of Openings"
            type="number"
            placeholder="e.g., 2"
            value={formData?.openings}
            onChange={(e) => onChange('openings', e?.target?.value)}
            error={errors?.openings}
            required
            min="1"
            description="How many positions are available"
          />

          <Input
            label="Application Deadline"
            type="date"
            value={formData?.deadline}
            onChange={(e) => onChange('deadline', e?.target?.value)}
            error={errors?.deadline}
            required
            min={new Date()?.toISOString()?.split('T')?.[0]}
            description="Last date to accept applications"
          />
        </div>
      </div>
    </div>
  );
};

export default JobDetailsSection;